package top.wcpe.allincore.ui.runtime.ir.prop

import top.wcpe.allincore.layout.engine.Dimension
import top.wcpe.allincore.layout.engine.Edges
import top.wcpe.allincore.ui.runtime.element.Button
import top.wcpe.allincore.ui.runtime.element.Dropdown
import top.wcpe.allincore.ui.runtime.element.Text
import top.wcpe.allincore.ui.runtime.element.TextTypography
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.internal.log.UiLog
import top.wcpe.allincore.ui.runtime.ir.eval.ValueCoercion
import top.wcpe.allincore.ui.runtime.theme.ThemeLookup

/**
 * v2 组件 token / 文本语义属性贡献者：`controlSize`、`textStyle`。
 *
 * 从当前 [PropApplyContext] 注入的 [ThemeLookup] 读取
 * `button.sizes.<name>.*` / `typography.<name>.*`，再落到既有布局/视觉属性。
 * 缺 token 时静默跳过对应字段，避免博物馆场景因未注册 key 刷屏 WARN。
 */
@Suppress("TooManyFunctions")
internal object ComponentTokenPropContributor {

    private val LOGGER = UiLog("top.wcpe.allincore.ui.runtime.ir.prop.ComponentTokenPropContributor")

    fun contribute(m: MutableMap<String, PropBinding>) {
        m["controlSize"] = PropBinding.writeOnly { el, v ->
            val name = v?.toString()?.trim().orEmpty()
            if (name.isEmpty()) return@writeOnly
            val lookup = PropApplyContext.currentThemeLookup()
            if (lookup == null) {
                LOGGER.warn("controlSize=$name 缺少主题 lookup，已忽略（位置：${PropApplyContext.currentLabel()}）")
                return@writeOnly
            }
            applyControlSize(el, name, lookup)
        }
        m["textStyle"] = PropBinding.writeOnly { el, v ->
            val name = v?.toString()?.trim().orEmpty()
            if (name.isEmpty()) return@writeOnly
            val lookup = PropApplyContext.currentThemeLookup()
            if (lookup == null) {
                // 无主题 lookup 时回退 v0.20.0 语义：textStyle → 内建 typography 角色（VisualPropContributor 同款）。
                if (el is Text) {
                    val typo = TextTypography.tryParse(name) ?: run {
                        LOGGER.warn("非法 textStyle: $name，已回退为 bodyMedium（位置：${PropApplyContext.currentLabel()}）")
                        TextTypography.BODY_MEDIUM
                    }
                    el.typography(typo)
                }
                return@writeOnly
            }
            applyTextStyle(el, name, lookup)
        }
    }

    private fun applyControlSize(el: UIElement<*>, name: String, lookup: ThemeLookup) {
        val kind = controlKindOf(el) ?: run {
            PropDiag.unsupported(LOGGER, el, "controlSize")
            return
        }
        val prefix = "$kind.sizes.$name"
        val height = lookup.getNumber("$prefix.height", Double.NaN)
        if (!height.isNaN()) {
            el.height(Dimension.px(height.toFloat()))
        }
        val paddingX = lookup.getNumber("$prefix.paddingX", Double.NaN)
        val paddingY = lookup.getNumber("$prefix.paddingY", Double.NaN)
        if (!paddingX.isNaN() || !paddingY.isNaN()) {
            val current = el.layoutStyle().padding
            val px = if (paddingX.isNaN()) current.left() else paddingX.toFloat()
            val py = if (paddingY.isNaN()) current.top() else paddingY.toFloat()
            el.padding(Edges(py, px, py, px))
        }
        val fontSize = lookup.getNumber("$prefix.fontSize", Double.NaN)
        if (!fontSize.isNaN()) {
            when (el) {
                is Button -> el.textScale(fontSize.toFloat())
                is Text -> el.size(fontSize.toFloat())
                is Dropdown -> el.size(fontSize.toFloat())
            }
        }
        // 无任何 token 也不报错：允许 DSL 先声明 size 名，主题后补。
    }

    private fun applyTextStyle(el: UIElement<*>, name: String, lookup: ThemeLookup) {
        if (el !is Text && el !is Button && el !is Dropdown) {
            PropDiag.unsupported(LOGGER, el, "textStyle")
            return
        }
        // 先尝试自定义 typography.<name>.*；再回退到内建 TextTypography 角色名。
        val size = lookup.getNumber("typography.$name.size", Double.NaN)
        if (!size.isNaN()) {
            when (el) {
                is Text -> el.size(size.toFloat())
                is Button -> el.textScale(size.toFloat())
                is Dropdown -> el.size(size.toFloat())
            }
        } else {
            TextTypography.tryParse(name)?.let { role ->
                when (el) {
                    is Text -> el.typography(role)
                    else -> Unit
                }
            }
        }
        val colorRaw = lookup.get("typography.$name.color")
        if (colorRaw != null) {
            val color = ValueCoercion.toIntColor(colorRaw)
            when (el) {
                is Text -> el.color(color)
                is Button -> el.color(color)
            }
        }
    }

    private fun controlKindOf(el: UIElement<*>): String? = when (el) {
        is Button -> "button"
        is Dropdown -> "dropdown"
        is Text -> "text"
        else -> null
    }
}
